import { graphql } from "@/__generated__";

export const MY_CONVERSATIONS = graphql(`
  query MyConversations {
    myConversations {
      id
      isSupportThread
      lastMessageAt
      unreadCount
      muted
      tutorCanCapture
      otherParticipant {
        id
        firstName
        lastName
        fullName
        initials
        role
        online
        tutorProfile { id teachingLanguage { id name code } }
      }
      lastMessage {
        id
        body
        deletedForEveryone
        createdAt
        sender { id }
      }
    }
  }
`);

export const CONVERSATION_MESSAGES = graphql(`
  query ConversationMessages($conversationId: ID!, $limit: Int) {
    conversationMessages(conversationId: $conversationId, limit: $limit) {
      id
      body
      messageType
      deliveredAt
      editedAt
      deletedAt
      deletedForEveryone
      createdAt
      read
      sender { id firstName lastName initials }
      replyToMessage {
        id
        body
        deletedForEveryone
        sender { id firstName }
        attachments { id kind filename url durationSeconds }
      }
      reactions { id reaction user { id } }
      attachments {
        id
        kind
        filename
        contentType
        byteSize
        durationSeconds
        url
        transcript
      }
      corrections {
        id
        originalBody
        correctedBody
        note
        author { id }
        createdAt
      }
      vocabularyEntries { id state outcome failureCode createdByRole sourceText correctedText correctionKind skipCorrection scheduledAt }
    }
  }
`);

export const TOGGLE_REACTION = graphql(`
  mutation ToggleReaction($input: ToggleReactionInput!) {
    toggleReaction(input: $input) {
      message {
        id
        reactions { id reaction user { id } }
      }
      errors
    }
  }
`);

export const TRANSLATE_MESSAGE = graphql(`
  mutation TranslateMessage($input: TranslateMessageInput!) {
    translateMessage(input: $input) {
      translation { id targetLang translatedBody provider }
      errors
    }
  }
`);

export const CREATE_MESSAGE_CORRECTION = graphql(`
  mutation CreateMessageCorrection($input: CreateMessageCorrectionInput!) {
    createMessageCorrection(input: $input) {
      correction { id originalBody correctedBody note author { id } createdAt }
      errors
    }
  }
`);

export const BLOCK_USER = graphql(`
  mutation BlockUser($input: BlockUserInput!) {
    blockUser(input: $input) { success errors }
  }
`);

// Who the viewer has blocked: the conversation menu offers "Unblock" instead of
// "Block" for them (#1169). Only the viewer's own blocks, never who blocked them.
export const CHAT_BLOCKED_USERS = graphql(`
  query ChatBlockedUsers {
    blockedUsers { id }
  }
`);

export const UNBLOCK_USER = graphql(`
  mutation UnblockUser($input: UnblockUserInput!) {
    unblockUser(input: $input) { success errors }
  }
`);

export const MUTE_CONVERSATION = graphql(`
  mutation MuteConversation($input: MuteConversationInput!) {
    muteConversation(input: $input) {
      conversation { id muted }
      errors
    }
  }
`);

export const UPDATE_NOTIFICATION_SETTINGS = graphql(`
  mutation UpdateNotificationSettings($input: UpdateNotificationSettingsInput!) {
    updateNotificationSettings(input: $input) {
      user { id }
      errors
    }
  }
`);

// The full entry comes back so the popup can hand it to whoever needs the
// server truth (id for Undo; state/outcome for the bubble's underline after
// the refetch). Selection repeated verbatim — fragment masking is on.
export const SAVE_VOCABULARY_ENTRY = graphql(`
  mutation SaveVocabularyEntry($input: SaveVocabularyEntryInput!) {
    saveVocabularyEntry(input: $input) {
      vocabularyEntry {
        id state outcome failureCode lessonId sourceMessageId createdByRole flashcardIds deckId sourceText correctedText correctionKind skipCorrection scheduledAt createdAt updatedAt
      }
      outcome
      reused
      pending
      errorCode
      errors
    }
  }
`);

export const SEARCH_MESSAGES = graphql(`
  query SearchMessages($conversationId: ID!, $q: String!) {
    searchMessages(conversationId: $conversationId, query: $q) {
      id
      body
      createdAt
      sender { id firstName lastName initials }
    }
  }
`);

export const FIND_OR_CREATE_CONVERSATION = graphql(`
  mutation FindOrCreateConversation($input: FindOrCreateConversationInput!) {
    findOrCreateConversation(input: $input) {
      conversation { id }
      errorCode
      errors
    }
  }
`);

export const FIND_OR_CREATE_LESSON_CONVERSATION = graphql(`
  mutation FindOrCreateLessonConversation($input: FindOrCreateLessonConversationInput!) {
    findOrCreateLessonConversation(input: $input) {
      conversation {
        id
        muted
        tutorCanCapture
        otherParticipant {
          id
          firstName
          lastName
          fullName
          initials
          role
          online
          tutorProfile { id teachingLanguage { id name code } }
        }
      }
      errors
    }
  }
`);

export const SEND_MESSAGE = graphql(`
  mutation SendMessage($input: SendMessageInput!) {
    sendMessage(input: $input) {
      message {
        id
        body
        messageType
        deliveredAt
        createdAt
        sender { id firstName lastName initials }
      }
      errorCode
      errors
    }
  }
`);

export const HEARTBEAT = graphql(`
  mutation Heartbeat {
    heartbeat(input: {}) { success }
  }
`);

export const EDIT_MESSAGE = graphql(`
  mutation EditMessage($input: EditMessageInput!) {
    editMessage(input: $input) {
      message { id body editedAt }
      errors
    }
  }
`);

export const DELETE_MESSAGE = graphql(`
  mutation DeleteMessage($input: DeleteMessageInput!) {
    deleteMessage(input: $input) {
      message { id deletedAt deletedForEveryone body }
      errors
    }
  }
`);

export const MARK_CONVERSATION_READ = graphql(`
  mutation MarkConversationRead($input: MarkConversationReadInput!) {
    markConversationRead(input: $input) {
      success
      errors
    }
  }
`);

export const SET_TYPING = graphql(`
  mutation SetTyping($input: SetTypingInput!) {
    setTyping(input: $input) {
      success
    }
  }
`);

export const MESSAGE_ADDED = graphql(`
  subscription MessageAdded($conversationId: ID!) {
    messageAdded(conversationId: $conversationId) {
      id
      body
      messageType
      createdAt
      deliveredAt
      sender { id firstName lastName initials }
    }
  }
`);

export const MESSAGE_UPDATED = graphql(`
  subscription MessageUpdated($conversationId: ID!) {
    messageUpdated(conversationId: $conversationId) {
      id
      body
      editedAt
      deletedAt
      deletedForEveryone
      read
    }
  }
`);

export const TYPING_CHANGED = graphql(`
  subscription TypingChanged($conversationId: ID!) {
    typingChanged(conversationId: $conversationId) {
      userId
      isTyping
    }
  }
`);

export const CONVERSATION_UPDATED = graphql(`
  subscription ConversationUpdated($userId: ID!) {
    conversationUpdated(userId: $userId) {
      id
      lastMessageAt
      unreadCount
      lastMessage {
        id
        body
        deletedForEveryone
        createdAt
        sender { id }
      }
    }
  }
`);
